import { Dispatch, useEffect, useRef } from 'react';
import { AppAction } from './AppContext';
import { pingApi, loadEventData as loadEventDataFromApi } from './eventApi';
import { useAuth } from './AuthContext';

// Note: AppAction type is imported from AppContext to keep action shape consistent.
// This hook mirrors the previous implementation but lives in its own file.

export default function useEventPolling(
  dispatch: Dispatch<AppAction>,
  logout: () => void,
  isAuthenticated: boolean
) {
  const timeoutRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  const intervalRef = useRef<number>(Number(import.meta.env.VITE_EVENT_REFRESH_MS) || 60000);
  const baseInterval = Number(import.meta.env.VITE_EVENT_REFRESH_MS) || 60000;
  const maxInterval = Number(import.meta.env.VITE_EVENT_REFRESH_MAX_MS) || 300000;
  const backoffFactor = Number(import.meta.env.VITE_EVENT_REFRESH_BACKOFF_FACTOR) || 2;

  useEffect(() => {
    let mounted = true;

    const runOnce = async () => {
      if (!mounted) return;
      if (!isAuthenticated) return;
      if (document.hidden) return;
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      try {
        await loadEventDataFromApi(dispatch as any, logout as any, { silent: true });
        intervalRef.current = baseInterval;
      } catch (e) {
        intervalRef.current = Math.min(maxInterval, Math.floor(intervalRef.current * backoffFactor));
        console.warn('useEventPolling (external): error while loading event data, backing off to', intervalRef.current, e);
      } finally {
        inFlightRef.current = false;
        if (mounted) {
          timeoutRef.current = window.setTimeout(runOnce, intervalRef.current);
        }
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        intervalRef.current = baseInterval;
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        void runOnce();
      }
    };

    const onOnline = async () => {
      console.debug('useEventPolling (external): navigator.onLine -> online');
      try {
        const ok = await pingApi();
        if (ok) {
          dispatch({ type: 'SET_ONLINE', payload: true } as any);
          intervalRef.current = baseInterval;
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          if (!document.hidden && isAuthenticated) {
            void runOnce();
          }
        } else {
          dispatch({ type: 'SET_ONLINE', payload: false } as any);
          intervalRef.current = Math.min(maxInterval, Math.floor(intervalRef.current * backoffFactor));
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          timeoutRef.current = window.setTimeout(runOnce, intervalRef.current);
        }
      } catch (e) {
        console.warn('useEventPolling (external): ping threw after online event', e);
        dispatch({ type: 'SET_ONLINE', payload: false } as any);
        intervalRef.current = Math.min(maxInterval, Math.floor(intervalRef.current * backoffFactor));
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        timeoutRef.current = window.setTimeout(runOnce, intervalRef.current);
      }
    };

    const onOffline = () => {
      console.debug('useEventPolling (external): navigator.onLine -> offline');
      // Mark app as offline immediately so UI can react
      dispatch({ type: 'SET_ONLINE', payload: false } as any);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    timeoutRef.current = window.setTimeout(runOnce, baseInterval);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      mounted = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [dispatch, logout, isAuthenticated, baseInterval, maxInterval, backoffFactor]);
}
